import _ from 'underscore';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, FluentProvider, Menu, MenuButton, MenuItem, MenuList, MenuPopover, MenuTrigger, webLightTheme } from '@fluentui/react-components';
import { MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-message-bar';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LinkedInImageType, LinkedInImageTypeMinSize } from '../consts';

const MediaEditorConfirmationDialog = ({ open, onConfirm, onCancel }) => {
  const i18n = useContext(I18nContext);

  return (
    <Dialog open={open}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {i18n.getString(_TL_('Are you sure you want to change your aspect ratio for cropping images?'))}
          </DialogTitle>
          <DialogContent>
            {i18n.getString(_TL_('Please note that the aspect ratio you choose for cropping will impact all placements where your ad serves.'))}
          </DialogContent>
          <DialogActions>
            <DialogTrigger>
              <Button appearance="primary" onClick={onConfirm}>
                {i18n.getString(_TL_('Yes'))}
              </Button>
            </DialogTrigger>
            <DialogTrigger>
              <Button appearance="secondary" onClick={onCancel}>
                {i18n.getString(_TL_('No'))}
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

MediaEditorConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const useAvailableAspectRatioItems = (adData) => {
  const i18n = useContext(I18nContext);

  const baseImage = useMemo(() => _.get(adData, ['Images', 'ImageList', 0, 0, 'baseImage'], {}), [adData]);

  const allItems = useMemo(() => [
    { type: LinkedInImageType.Landscape, label: i18n.getString(_TL_('Horizontal (1.91:1 — 1200 x 628 pixels)')) },
    { type: LinkedInImageType.Vertical, label: i18n.getString(_TL_('Vertical (1.1.91 — 628 x 900 pixels)')) },
    { type: LinkedInImageType.Square, label: i18n.getString(_TL_('Square (1:1 — 1200 x 12000 pixels)')) },
  ], [i18n]);

  return allItems.filter(({ type }) => {
    const { width, height } = baseImage;
    const [minWidth, minHeight] = LinkedInImageTypeMinSize[type];

    return (width >= minWidth && height >= minHeight);
  });
};

export const MediaEditorControls = ({
  adData,
  imageType,
  onSelectedAspectRatioChange,
}) => {
  const i18n = useContext(I18nContext);

  const [currentImageType, setCurrentImageType] = useState();
  const [newCurrentImageType, setNewCurrentImageType] = useState();

  const items = useAvailableAspectRatioItems(adData);
  const showConfirmationDialog = useCallback((newImageType) => {
    if (newImageType === currentImageType) { return; }

    setNewCurrentImageType(newImageType);
  }, [currentImageType]);

  const cancelNewImageTypeSelection = useCallback(() => {
    setNewCurrentImageType(undefined);
  }, []);

  const confirmNewImageTypeSelection = useCallback(() => {
    onSelectedAspectRatioChange(newCurrentImageType);
    setCurrentImageType(newCurrentImageType);

    setNewCurrentImageType(undefined);
  }, [newCurrentImageType, onSelectedAspectRatioChange]);

  useEffect(() => {
    if (!imageType || currentImageType) { return; }

    onSelectedAspectRatioChange(imageType);
    setCurrentImageType(imageType);
  }, [currentImageType, imageType, onSelectedAspectRatioChange]);

  return (
    <FluentProvider theme={webLightTheme}>
      <Menu>
        <MenuTrigger>
          <MenuButton appearance="primary">{i18n.getString(_TL_('Crop'))}</MenuButton>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {items.map(({ type, label }) => (
              <MenuItem key={type} onClick={() => showConfirmationDialog(type)}>
                {label}
              </MenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
      <MessageBar style={{ position: 'relative', width: 'max-content', margin: '8px 0 32px' }}>
        <MessageBarBody>
          <MessageBarTitle>
            {i18n.getString(_TL_('Note'))}
          </MessageBarTitle>
          {i18n.getString(_TL_('Cropping is limited for LinkedIn campaigns. Please note that the aspect ratio you choose for cropping will impact all placements where your ad serves.'))}
        </MessageBarBody>
      </MessageBar>
      <MediaEditorConfirmationDialog
        open={!!newCurrentImageType}
        onConfirm={confirmNewImageTypeSelection}
        onCancel={cancelNewImageTypeSelection}
      />
    </FluentProvider>
  );
};

MediaEditorControls.propTypes = {
  adData: PropTypes.shape({}).isRequired,
  imageType: PropTypes.string.isRequired,
  onSelectedAspectRatioChange: PropTypes.func.isRequired,
};
